/**
 * The documento field — deliberately unformatted.
 *
 * ⚠️ THIS FIELD MUST NOT MASK, FORMAT, OR VALIDATE BY SHAPE.
 *
 * It previously applied a CPF/CNPJ mask driven by digit count, and stripped the
 * input with `replace(/\D/g, '')`. That erased every letter — and because the
 * stripped value fed back through `value={raw}` on the next keystroke, the
 * canonical value degraded to digits-only after one round trip. A Chilean RUT
 * (`12.345.678-K`) or a passport (`AB1234567`) could not be typed at all.
 *
 * It also contradicted open question 1, which had already settled the rule:
 * accept any document, normalize, and never infer the type from length —
 * "an 11-character non-CPF identifier would be rejected for failing a check it
 * was never subject to."
 *
 * So: what the customer types is what they see. Normalization happens exactly
 * once, in packages/documento, and only the canonical form is submitted.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCUMENTO_MAX_LENGTH, normalizeDocumento } from '@my-newshoes/documento';

import styles from './DocumentoInput.module.css';

interface Props {
  value: string;
  /** Receives the raw text as typed, and its canonical form. Submit the canonical. */
  onChange: (raw: string, canonical: string) => void;
  autoFocus?: boolean;
}

export function DocumentoInput({ value, onChange, autoFocus }: Props): React.JSX.Element {
  const id = useId();
  const { t } = useTranslation();

  return (
    <div className={styles['input-group']}>
      <label htmlFor={id} className={styles['label']}>
        {t('field.documento')}
      </label>
      <input
        id={id}
        className={styles['input']}
        type="text"
        // `text`, never `numeric`: a digits-only soft keyboard makes the `K` of
        // a RUT and the letters of a passport physically untypeable on mobile.
        inputMode="text"
        // Documents are case-insensitive and normalization uppercases anyway;
        // capitalising as they type means what they see matches what is stored.
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        autoComplete="username"
        // The contract bound (32), not a CPF-shaped guess. Punctuation the user
        // types is stripped by normalization, so the raw field needs headroom
        // above the canonical limit.
        maxLength={DOCUMENTO_MAX_LENGTH + 8}
        {...(autoFocus === true ? { autoFocus: true } : {})}
        value={value}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw, normalizeDocumento(raw));
        }}
        placeholder={t('field.documentoPlaceholder')}
        aria-describedby={`${id}-hint`}
      />
      {/* Two different jobs, two keys. The placeholder names the LOCAL document
          for this market plus passport, and must fit an input; the hint answers
          the question customers actually have — "which document?" — with the
          one true answer: whatever the store registered. A list of every
          document the platform accepts fit neither, and grew with each country. */}
      <p id={`${id}-hint`} className={styles['hint']}>
        {t('field.documentoHint')}
      </p>
    </div>
  );
}
